import { addMinutes, subMinutes } from "date-fns"
import { AttendanceEvent } from "@/entities/attendanceEvent/AttendanceEventType"
import { YoungWithAttendanceAndMetadata } from "../pages/admin/FaireLAppel"
import { attendanceTimeToDate } from "../types/AttendanceTime"
import { DocumentData } from "../types/firestoreDocument"
import { GlobalSettings } from "@/entities/globalSettings/globalSettings"
import { logger } from "@/utils/logger"
import { getLastEventUsedToComputeAttendanceAtTimeForRowCall } from "./attendanceCompute"
import { runTransactionBatch } from "@/firestore/transactions"

type FaireLAppelSubmitParams = {
   youngsWithOverrides: YoungWithAttendanceAndMetadata[]
   adminIdReporting: string
   now: Date
   currentSchoolYearId: string
   globalSettings: GlobalSettings
   createAttendanceEvent: (data: DocumentData<AttendanceEvent>) => void
   updateAttendanceEvent: (id: string, data: Partial<DocumentData<AttendanceEvent>>) => void
}

function computeDateFromTypeAndTime(
   young: YoungWithAttendanceAndMetadata,
   now: Date,
   globalSettings: GlobalSettings,
) {
   let from, to

   if (young.newStatus === "present") {
      from = attendanceTimeToDate(globalSettings, young.presentFromType, now, young.presentFromTime)
      // By default, we suppose the young is present until the end of the day
      to = attendanceTimeToDate(globalSettings, "end_of_day", now)
   } else if (young.newStatus === "late") {
      from = attendanceTimeToDate(globalSettings, young.lateFromType, now, young.lateFromTime)
      to = addMinutes(from, 15)
   } else if (young.newStatus === "absent") {
      from = attendanceTimeToDate(globalSettings, young.absenceFromType, now, young.absenceFromTime)
      to = attendanceTimeToDate(globalSettings, young.absenceToType, now, young.absenceToTime)
   }

   return { from, to } as { from: Date; to: Date }
}

export const doesYoungHaveOverrides = (young: YoungWithAttendanceAndMetadata) =>
   young.newStatus !== young.current_attendance_status ||
   young.newExpected !== young.is_current_attendance_expected

export async function handleFaireLAppelSubmit({
	youngsWithOverrides,
	now,
	currentSchoolYearId,
	globalSettings,
   createAttendanceEvent,
   updateAttendanceEvent,
   
}: FaireLAppelSubmitParams) {
   const updatedYoungs = youngsWithOverrides.filter(doesYoungHaveOverrides)
   logger('Handle Faire L\'Appel', { updatedYoungs })

   const errors: string[] = []

   await runTransactionBatch(async () => {
      for ( const young of updatedYoungs) {
         try {
            const { from, to } = computeDateFromTypeAndTime(young, now, globalSettings)

            const isJustified =
               young.newStatus === "present" ||
               young.newStatus === "external" ||
               (young.newStatus === "late" && young.newExpected)

            // Détail de la logique :
            // - Si un jeune passe de 'absent' à 'present' dans les 15 minutes suivant le début de l'absence,
            //   alors l'événement d'absence est transformé en 'late' (retard) sur la même période (15min).
            // - Sinon, on applique la logique normale de création d'événement selon le statut choisi.
            // Les fonctions fetchAbsenceEventsForYoung et updateAttendanceEventToLate sont des placeholders à remplacer.
            
            // Si le statut passe de 'absent' à 'present', vérifier les conditions de transformation en 'late'
            if (
               young.current_attendance_status === "absent" &&
               young.newStatus === "present"
            ) {

               // Récupérer les événements d'absence non justifiés pour ce jeune à la date concernée
               const lastEvent = await getLastEventUsedToComputeAttendanceAtTimeForRowCall({
                  young_id: young.id,
                  time: now,
                  schoolyear_id: currentSchoolYearId,
               })

               // Si l'événement est un absence, vérifier si le retour à "present" intervient dans les 15 minutes
               if (lastEvent && lastEvent.type === "absent") {
                  const absenceStart = lastEvent.from
                  const presentTime = from
                  const diffMinutes = (presentTime.getTime() - absenceStart.getTime()) / 60000
                  if (diffMinutes >= 0 && diffMinutes <= 15) {
                     // Transformer l'absence en retard
                     await updateAttendanceEvent(
                        lastEvent.id,
                        {
                           type: "late",
                           from: subMinutes(from, 15),
                           delayMinutes: 15,
                           to: from,
                        }
                     )
                     // On ne crée pas d'événement "present" dans ce cas et on passe au jeune suivant
                     continue
                  }
               }
            }

            // Sinon, logique normale de création d'événement
            const attendanceEvent: DocumentData<AttendanceEvent> = {
               school_year_id: currentSchoolYearId,
               young_id: young.id,
               type: young.newStatus,
               justified: isJustified,
               from,
               delayMinutes: 15,
               to,
               justification_validation_status: isJustified ? "validated" : "upload_pending",
               admin_comment: "",
               expected: young.newExpected,
            }
            
            await createAttendanceEvent(attendanceEvent)
         
         } catch (error) {
            console.error('Error when handling a young', young, error);
            errors.push(`${young.firstName} ${young.lastName} : ${error}`)
         } 
      }
   })

   if (errors.length > 0) {
      throw new Error("Erreurs d'enregistrement pour les jeunes :\n" + errors.join("\n"))
   }
}
